/**
 * RFC 9457 `application/problem+json` — the only error shape the erpx API emits
 * (CLAUDE.md §7).
 *
 * The reason this lives in the shared library rather than in each app: `EField`
 * needs a way to be handed a server field error and wire it onto the right
 * control with `aria-invalid` and `aria-describedby`. That contract is only
 * worth anything if all three apps agree on the payload shape, so the shape is
 * defined once, here, together with the stable machine codes worth branching on.
 */

/** Stable machine codes from CLAUDE.md §7. Branch on these, never on prose. */
export const ERROR_CODES = {
  periodClosed: 'ERR_PERIOD_CLOSED',
  budgetExceeded: 'ERR_BUDGET_EXCEEDED',
  duplicateInvoice: 'ERR_DUPLICATE_INVOICE',
  creditLimit: 'ERR_CREDIT_LIMIT',
  sodViolation: 'ERR_SOD_VIOLATION',
  rowVersion: 'ERR_ROW_VERSION',
  /** §8.9: invoice, purchase order and receipt disagree beyond tolerance. */
  threeWayMatch: 'ERR_THREE_WAY_MATCH',
  validation: 'ERR_VALIDATION',
  forbidden: 'ERR_FORBIDDEN',
  unauthenticated: 'ERR_UNAUTHENTICATED',
  notFound: 'ERR_NOT_FOUND',
  rateLimited: 'ERR_RATE_LIMITED',
  stepUpRequired: 'ERR_STEP_UP_REQUIRED',
  /** No second factor enrolled — the answer to a step-up is "enrol", not "retry". */
  mfaRequired: 'ERR_MFA_REQUIRED',
  /** Credential door held shut after repeated failures — not a wrong password. */
  lockedOut: 'ERR_LOCKED_OUT',
  tenantSuspended: 'ERR_TENANT_SUSPENDED',
} as const

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]

export interface ProblemFieldError {
  /** Dotted path into the request body, e.g. `lines.3.quantity`. */
  field: string
  /** Machine code for this field, e.g. `required`, `gt`, `oneof`. */
  code?: string
  message: string
  /**
   * Machine-readable operands behind `message`, when the server sent any.
   *
   * `message` is a sentence in the server's language; `params` is what it is about.
   * A screen that needs to mark the offending row — the budget line that broke its
   * limit, the purchase-order line outside tolerance — reads ids and figures from
   * here rather than parsing English, which would stop working the first time the
   * message is translated (§6 ships a Dhivehi catalogue).
   *
   * Values are strings for the same reason money is: a JSON number cannot carry
   * NUMERIC(18,2) without a client turning it into a float on the way in (§0.4).
   */
  params?: Record<string, string>
}

export interface ProblemDetails {
  /** URI reference identifying the problem type. */
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  /** erpx extension: the stable machine code (see {@link ERROR_CODES}). */
  code?: string
  /** erpx extension: per-field validation failures. */
  errors?: ProblemFieldError[]
  /** erpx extension: correlates with the server's structured log line. */
  traceId?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Narrow an unknown thrown value to a problem+json body. */
export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (!isRecord(value)) return false
  return typeof value['title'] === 'string' && typeof value['status'] === 'number'
}

/** The problem body carried by a thrown error, if there is one. */
export function toProblemDetails(error: unknown): ProblemDetails | null {
  if (isProblemDetails(error)) return error
  if (isRecord(error) && isProblemDetails(error['problem'])) return error['problem']
  return null
}

/**
 * Collapse field errors into `field -> messages`, so a form can look up its
 * controls by name in one pass instead of scanning the array per field.
 */
export function fieldErrorMap(problem: ProblemDetails | null | undefined): Record<string, string[]> {
  const map: Record<string, string[]> = {}
  if (!problem?.errors) return map

  for (const entry of problem.errors) {
    if (typeof entry.field !== 'string' || entry.field === '') continue
    const bucket = map[entry.field]
    if (bucket) bucket.push(entry.message)
    else map[entry.field] = [entry.message]
  }
  return map
}

/** First message for a field, or `undefined`. Feeds straight into `EField`. */
export function fieldError(
  problem: ProblemDetails | null | undefined,
  field: string,
): string | undefined {
  return problem?.errors?.find((entry) => entry.field === field)?.message
}

export function hasCode(problem: ProblemDetails | null | undefined, code: string): boolean {
  return problem?.code === code
}

/**
 * A sentence to show the user. Prefers `detail` (written for humans) and falls
 * back to `title`; never returns raw JSON, and never returns an empty string.
 */
export function problemMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const problem = toProblemDetails(error)
  if (problem) {
    const detail = problem.detail?.trim()
    if (detail) return detail
    const title = problem.title.trim()
    if (title) return title
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message
  return fallback
}

/**
 * Whether retrying the same request could plausibly succeed. 408/429 and 5xx
 * are worth a retry; a 4xx caused by the payload is not.
 */
export function isRetryableProblem(error: unknown): boolean {
  const problem = toProblemDetails(error)
  if (!problem) return true
  if (problem.status === 408 || problem.status === 429) return true
  return problem.status >= 500
}

/** A 409 from optimistic locking — the app reloads and asks for a reapply. */
export function isRowVersionConflict(error: unknown): boolean {
  const problem = toProblemDetails(error)
  return problem?.status === 409 && problem.code === ERROR_CODES.rowVersion
}
